/**
 * Dates de livraison estimées de la frise de la fiche montre (sections/delivery-timeline.liquid).
 *
 * Calcul à l'heure de Paris, quel que soit le fuseau du visiteur, et dans son navigateur : la page
 * peut sortir du cache de Shopify, une date calculée par le serveur serait périmée.
 *
 * - Point de départ : aujourd'hui si c'est un jour d'expédition et qu'il est avant l'heure limite,
 *   sinon le jour d'expédition suivant.
 * - Jours d'expédition : lundi au vendredi, plus le samedi si le réglage le dit, hors jours fériés
 *   français (réglage) et hors fermeture de l'atelier.
 * - En stock : expédiée le jour du départ (réglage « le jour même », décision du 11 octobre 2026),
 *   sinon N jours d'expédition après (48 h = 2 jours).
 *   À la commande : expédiée entre départ + min et départ + max jours calendaires, reporté au jour
 *   d'expédition suivant si besoin.
 * - Transport : délai réel des dates (2 à 5 jours ouvrés), lundi au vendredi, hors jours fériés. Les
 *   textes du site gardent leur délai affiché (3 à 5 jours ouvrés), plus prudent.
 *
 * Réglages : Paramètres du thème > Délais de livraison. Sans JavaScript, la frise garde
 * « + 3 à 5 jours ouvrés ».
 */
(() => {
  const DAY = 86400000;

  /** Today's calendar date (UTC midnight) and the hour, in Paris. */
  const parisNow = () => {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .forEach((part) => {
        parts[part.type] = part.value;
      });
    return { date: Date.UTC(+parts.year, +parts.month - 1, +parts.day), hour: +parts.hour };
  };

  /** Easter Sunday (Meeus / Jones / Butcher). */
  const easter = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return Date.UTC(year, month - 1, day);
  };

  const holidayCache = {};
  /** French public holidays of a year, Whit Monday included. */
  const holidays = (year) => {
    if (!holidayCache[year]) {
      const e = easter(year);
      holidayCache[year] = new Set([
        Date.UTC(year, 0, 1),
        e + DAY,
        Date.UTC(year, 4, 1),
        Date.UTC(year, 4, 8),
        e + 39 * DAY,
        e + 50 * DAY,
        Date.UTC(year, 6, 14),
        Date.UTC(year, 7, 15),
        Date.UTC(year, 10, 1),
        Date.UTC(year, 10, 11),
        Date.UTC(year, 11, 25),
      ]);
    }
    return holidayCache[year];
  };

  const parseDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
    return match ? Date.UTC(+match[1], +match[2] - 1, +match[3]) : null;
  };

  const estimate = (config) => {
    const isHoliday = (t) => config.skipHolidays && holidays(new Date(t).getUTCFullYear()).has(t);
    const isClosed = (t) =>
      config.closedFrom !== null && config.closedTo !== null && t >= config.closedFrom && t <= config.closedTo;
    const isShipDay = (t) => {
      const weekday = new Date(t).getUTCDay();
      return weekday !== 0 && (weekday !== 6 || config.shipSaturday) && !isHoliday(t) && !isClosed(t);
    };
    const isTransitDay = (t) => {
      const weekday = new Date(t).getUTCDay();
      return weekday !== 0 && weekday !== 6 && !isHoliday(t);
    };
    const add = (t, count, test) => {
      let day = t;
      let left = count;
      while (left > 0) {
        day += DAY;
        if (test(day)) left -= 1;
      }
      return day;
    };
    const sameOrNext = (t, test) => {
      let day = t;
      while (!test(day)) day += DAY;
      return day;
    };

    const now = parisNow();
    const start =
      isShipDay(now.date) && now.hour < config.cutoff ? now.date : sameOrNext(now.date + DAY, isShipDay);

    let shipFirst;
    let shipLast;
    if (config.mode === 'stock') {
      shipFirst = config.sameDay ? start : add(start, config.stockDays, isShipDay);
      shipLast = shipFirst;
    } else {
      shipFirst = sameOrNext(start + config.orderMin * DAY, isShipDay);
      shipLast = sameOrNext(start + config.orderMax * DAY, isShipDay);
    }
    return [add(shipFirst, config.transitMin, isTransitDay), add(shipLast, config.transitMax, isTransitDay)];
  };

  /** « Du 17 au 21 oct. », « Du 30 oct. au 3 nov. », « Oct 17 – 21 ». */
  const formatRange = (first, last, config) => {
    const french = config.lang.startsWith('fr');
    const a = new Date(first);
    const b = new Date(last);
    const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
    const month = (d) => new Intl.DateTimeFormat(config.lang, { timeZone: 'UTC', month: 'short' }).format(d);
    const full = (d) =>
      french
        ? `${d.getUTCDate() === 1 ? '1er' : d.getUTCDate()} ${month(d)}`
        : new Intl.DateTimeFormat(config.lang, { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(d);
    const dayOnly = (d) => (french && d.getUTCDate() === 1 ? '1er' : String(d.getUTCDate()));

    const startText = sameMonth && french ? dayOnly(a) : full(a);
    const endText = sameMonth && !french ? dayOnly(b) : full(b);
    return config.range.replace('[debut]', startText).replace('[fin]', endText);
  };

  const run = () => {
    document.querySelectorAll('[data-delivery-estimate]').forEach((list) => {
      const target = list.querySelector('[data-delivery-estimate-target]');
      if (!target) return;
      const data = list.dataset;
      const config = {
        mode: data.deliveryEstimate,
        cutoff: Number(data.cutoff),
        sameDay: data.sameDay === 'true',
        shipSaturday: data.shipSaturday === 'true',
        skipHolidays: data.skipHolidays === 'true',
        closedFrom: parseDate(data.closedFrom),
        closedTo: parseDate(data.closedTo),
        stockDays: Number(data.stockDays),
        orderMin: Number(data.orderMin),
        orderMax: Number(data.orderMax),
        transitMin: Number(data.transitMin),
        transitMax: Number(data.transitMax),
        range: data.range || '[debut] – [fin]',
        lang: data.lang || document.documentElement.lang || 'fr',
      };
      const numbers = [config.cutoff, config.stockDays, config.orderMin, config.orderMax, config.transitMin, config.transitMax];
      if (numbers.some((n) => !Number.isFinite(n) || n < 0)) return;
      try {
        const [first, last] = estimate(config);
        target.textContent = formatRange(first, last, config);
      } catch (error) {
        // Keep the server text (« + 3 à 5 jours ouvrés ») if the browser lacks Intl time zones.
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
